import React, { useEffect, useRef, useState } from "react";
import { timeToSec, secToHMS } from "../../utils/time";
import { REEL_FRAME_THEMES, DEFAULT_REEL_FRAME_THEME_ID } from "../../constants/reelFrameThemes";
import {
  FaChevronDown, FaChevronRight, FaPlus, FaTrash,
  FaPlay, FaFilm, FaCheckCircle, FaSpinner, FaExclamationTriangle, FaClock,
} from "react-icons/fa";

// Story editing card shared by the Episode Metadata Editor and the Episode
// Editor (moved out of pages/EpisodeMetadataEditor.js, 2026-09-28). Saving is
// delegated to the parent's onSaveField (see hooks/useStoryEditing.js).
// hideTimestamps: the Episode Editor edits story start/end on its timeline,
// so the card must not keep a second, independently-edited copy.

const GENRE_SUGGESTIONS = [
  "హాస్యం", "భావోద్వేగం", "భయానక", "చారిత్రిక", "పిల్లల కథ", "ప్రేమకథ", "సాంఘిక",
];

// Extracted once from performances.analysis_text — same regex the Python
// pipeline uses (pipeline/reels/analysis.py: extract_punch_lines), kept in
// sync by hand since this is JS/Python duplication of a small, stable rule.
const PUNCHLINE_SECTION_RE = /పంచ్\s*లైన్స్\s*\)?\s*:?\s*([\s\S]*?)(?:కథ\s*చెప్పిన\s*తీరు|$)/;
const QUOTED_RE = /['‘’]([^'‘’]{4,200})['‘’]/g;

function extractPunchlinesFromAnalysis(analysisText) {
  if (!analysisText) return [];
  const m = PUNCHLINE_SECTION_RE.exec(analysisText);
  if (!m) return [];
  const out = [];
  let qm;
  QUOTED_RE.lastIndex = 0;
  while ((qm = QUOTED_RE.exec(m[1])) !== null) {
    const t = qm[1].trim();
    if (t) out.push({ text: t, approx_ts_sec: null, auditory_highlight: false });
  }
  return out;
}

// ─── Small building blocks ──────────────────────────────────────────────

const SaveIndicator = ({ status }) => {
  if (!status) return null;
  if (status === "saving") return <FaSpinner className="animate-spin text-white/30 text-[11px]" title="Saving…" />;
  if (status === "saved") return <FaCheckCircle className="text-green-400/70 text-[11px]" title="Saved" />;
  if (status === "error") return <FaExclamationTriangle className="text-red-400/70 text-[11px]" title="Save failed" />;
  return null;
};

const FieldLabel = ({ children, status }) => (
  <div className="flex items-center gap-2 mb-1">
    <span className="text-xs font-semibold text-kk-gold/70">{children}</span>
    <SaveIndicator status={status} />
  </div>
);

// ─── Story start/end timestamp row (mirrors EpisodeTimestamps.js's
// TimestampSection UX: inline HH:MM:SS field + Set-from-player button) ──

const TimestampRow = ({ label, value, onChange, onSetFromPlayer, onPlay, color, saveStatus, onCommit }) => {
  const sec = timeToSec(value);
  const colorMap = {
    blue: { bg: "bg-blue-500/10", border: "border-blue-500/30", btn: "bg-blue-600 hover:bg-blue-500" },
    green: { bg: "bg-green-500/10", border: "border-green-500/30", btn: "bg-green-600 hover:bg-green-500" },
  };
  const c = colorMap[color] || colorMap.blue;
  return (
    <div className="flex items-center gap-2">
      <span className="text-[11px] text-white/40 w-10">{label}</span>
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        onBlur={(e) => { if (onCommit && timeToSec(e.target.value) != null) onCommit(e.target.value); }}
        placeholder="H:MM:SS"
        className={`w-24 px-2 py-1.5 ${c.bg} border ${c.border} rounded text-white text-sm font-mono text-center
          placeholder:text-white/20 focus:outline-none focus:ring-2 focus:ring-white/20`}
      />
      {sec != null && <span className="text-[10px] text-white/25 font-mono w-12">{sec}s</span>}
      <button onClick={onSetFromPlayer}
        className={`px-2.5 py-1.5 text-white text-[11px] rounded-lg font-semibold ${c.btn} transition-all active:scale-95 flex items-center gap-1`}>
        <FaClock className="text-[9px]" /> Set from player
      </button>
      {sec != null && (
        <button onClick={() => onPlay(sec)} className="p-1.5 text-kk-gold/50 hover:text-kk-gold transition-colors">
          <FaPlay className="text-[9px]" />
        </button>
      )}
      <SaveIndicator status={saveStatus} />
    </div>
  );
};

// ─── Punchlines editor ──────────────────────────────────────────────────

const PunchlinesEditor = ({ punchlines, onChange, onSeek }) => {
  const list = punchlines || [];

  const update = (i, patch) => {
    const next = list.map((p, idx) => (idx === i ? { ...p, ...patch } : p));
    onChange(next);
  };
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  const add = () => onChange([...list, { text: "", approx_ts_sec: null, auditory_highlight: false }]);

  return (
    <div className="space-y-2">
      {list.map((p, i) => (
        <div key={i} className="flex items-start gap-2 bg-white/[0.02] border border-white/5 rounded-lg p-2">
          <textarea
            value={p.text || ""}
            onChange={(e) => update(i, { text: e.target.value })}
            rows={2}
            placeholder="పంచ్ లైన్..."
            className="flex-1 px-2 py-1.5 bg-white/5 border border-white/10 rounded text-sm text-kk-cream
              placeholder:text-white/20 focus:outline-none focus:ring-1 focus:ring-kk-gold/40 resize-y"
          />
          <input
            type="text"
            value={p.approx_ts_sec ?? ""}
            onChange={(e) => update(i, { approx_ts_sec: e.target.value === "" ? null : Number(e.target.value) })}
            placeholder="sec"
            className="w-16 px-2 py-1.5 bg-white/5 border border-white/10 rounded text-xs text-center font-mono
              text-kk-cream placeholder:text-white/20 focus:outline-none focus:ring-1 focus:ring-kk-gold/40"
          />
          {p.approx_ts_sec != null && (
            <button onClick={() => onSeek(p.approx_ts_sec)} title="Play"
              className="p-1.5 text-kk-gold/50 hover:text-kk-gold transition-colors">
              <FaPlay className="text-[10px]" />
            </button>
          )}
          <button onClick={() => remove(i)} title="Remove"
            className="p-1.5 text-red-400/40 hover:text-red-400 transition-colors">
            <FaTrash className="text-[10px]" />
          </button>
        </div>
      ))}
      <button onClick={add}
        className="flex items-center gap-1.5 px-3 py-1.5 border border-dashed border-kk-gold/25 rounded-lg
          text-kk-gold/50 text-xs hover:text-kk-gold hover:border-kk-gold/40 transition-colors w-full justify-center">
        <FaPlus className="text-[9px]" /> Add punch line
      </button>
    </div>
  );
};

// ─── Highlight segments editor — shows both auto-identified (from the
// automatic pipeline) and manually-added segments, visually distinguished ──

const HighlightSegmentsEditor = ({ segments, onChange, currentTime, onSeek }) => {
  const list = segments || [];

  const update = (i, patch) => {
    const next = list.map((s, idx) => (idx === i ? { ...s, ...patch } : s));
    onChange(next);
  };
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  const add = () => onChange([...list, { start_sec: Math.floor(currentTime), end_sec: Math.floor(currentTime) + 10, label: `segment${list.length}`, source: "manual" }]);

  if (list.length === 0) {
    return (
      <div className="space-y-2">
        <p className="text-[11px] text-white/30 italic">
          No highlight segments yet — none were auto-identified for this story, or the automatic pass hasn't run yet.
        </p>
        <button onClick={add}
          className="flex items-center gap-1.5 px-3 py-1.5 border border-dashed border-kk-gold/25 rounded-lg
            text-kk-gold/50 text-xs hover:text-kk-gold hover:border-kk-gold/40 transition-colors w-full justify-center">
          <FaPlus className="text-[9px]" /> Add segment (at current player time)
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {list.map((s, i) => {
        const dur = (s.end_sec != null && s.start_sec != null) ? s.end_sec - s.start_sec : null;
        const isAuto = s.source === "auto";
        return (
          <div key={i} className={`flex items-center gap-2 border rounded-lg p-2 flex-wrap
            ${isAuto ? "bg-cyan-500/[0.04] border-cyan-500/15" : "bg-white/[0.02] border-white/5"}`}>
            <span className={`text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wide font-semibold
              ${isAuto ? "bg-cyan-500/15 text-cyan-300" : "bg-purple-500/15 text-purple-300"}`}>
              {isAuto ? "auto" : "manual"}
            </span>
            <input
              type="text"
              value={s.label || ""}
              onChange={(e) => update(i, { label: e.target.value })}
              placeholder="label"
              className="w-24 px-2 py-1.5 bg-white/5 border border-white/10 rounded text-xs text-kk-cream
                placeholder:text-white/20 focus:outline-none focus:ring-1 focus:ring-kk-gold/40"
            />
            <div className="flex items-center gap-1">
              <input
                type="number"
                value={s.start_sec ?? ""}
                onChange={(e) => update(i, { start_sec: Number(e.target.value) })}
                className="w-20 px-2 py-1.5 bg-blue-500/10 border border-blue-500/30 rounded text-xs text-center font-mono text-kk-cream"
              />
              <button onClick={() => update(i, { start_sec: Math.floor(currentTime) })}
                className="px-2 py-1.5 bg-blue-600 hover:bg-blue-500 rounded text-white text-[10px] font-semibold">
                Set Start
              </button>
            </div>
            <span className="text-white/20 text-xs">→</span>
            <div className="flex items-center gap-1">
              <input
                type="number"
                value={s.end_sec ?? ""}
                onChange={(e) => update(i, { end_sec: Number(e.target.value) })}
                className="w-20 px-2 py-1.5 bg-green-500/10 border border-green-500/30 rounded text-xs text-center font-mono text-kk-cream"
              />
              <button onClick={() => update(i, { end_sec: Math.floor(currentTime) })}
                className="px-2 py-1.5 bg-green-600 hover:bg-green-500 rounded text-white text-[10px] font-semibold">
                Set End
              </button>
            </div>
            {dur != null && (
              <span className="text-[10px] text-white/30 font-mono">{dur}s</span>
            )}
            <button onClick={() => onSeek(s.start_sec)} title="Play from start"
              className="p-1.5 text-kk-gold/50 hover:text-kk-gold transition-colors ml-auto">
              <FaPlay className="text-[10px]" />
            </button>
            <button onClick={() => remove(i)} title="Remove segment"
              className="p-1.5 text-red-400/40 hover:text-red-400 transition-colors">
              <FaTrash className="text-[10px]" />
            </button>
          </div>
        );
      })}
      <button onClick={add}
        className="flex items-center gap-1.5 px-3 py-1.5 border border-dashed border-kk-gold/25 rounded-lg
          text-kk-gold/50 text-xs hover:text-kk-gold hover:border-kk-gold/40 transition-colors w-full justify-center">
        <FaPlus className="text-[9px]" /> Add segment (at current player time)
      </button>
    </div>
  );
};

// ─── Frame theme picker ─────────────────────────────────────────────────

const FrameThemePicker = ({ value, onChange }) => (
  <div className="flex items-center gap-2 flex-wrap">
    {REEL_FRAME_THEMES.map((t) => {
      const selected = t.id === (value || DEFAULT_REEL_FRAME_THEME_ID);
      return (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          title={t.tagline}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs transition-all
            ${selected ? "border-kk-gold bg-kk-gold/10 text-kk-gold" : "border-white/10 text-white/50 hover:border-white/25"}`}
        >
          <span className="w-3 h-3 rounded-full border border-white/20" style={{ background: t.swatch }} />
          {t.name}
        </button>
      );
    })}
  </div>
);

// ─── Story card ──────────────────────────────────────────────────────────

// Local state that follows its prop when the prop changes (someone else
// saved), except while THIS user is editing that field — their typing wins.
function useSynced(propValue, fieldKey, collab, same = (a, b) => a === b) {
  const [v, setV] = useState(propValue);
  const prev = useRef(propValue);
  useEffect(() => {
    if (!same(propValue, prev.current)) {
      prev.current = propValue;
      if (collab?.focusRef?.current !== fieldKey) setV(propValue);
    }
  }, [propValue, fieldKey, collab, same]);
  return [v, setV];
}
const sameJSON = (a, b) => JSON.stringify(a) === JSON.stringify(b);

// Badge shown on a field someone else is editing (collaborative editor only).
const LockNote = ({ holder }) => (holder ? (
  <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold"
    style={{ background: `${holder.color}26`, color: holder.color }}>✎ {holder.name} is editing</span>
) : null);

const StoryCard = ({ perf, story, writerName, storyReel, onSaveField, onGenerateReel, onSeek, currentTime, saveStatus, hideTimestamps = false, collab = null }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [showText, setShowText] = useState(false); // transcript + analysis hidden by default

  const k = (field) => `${perf.performance_id}:${field}`;
  // collab.bind(key) -> {holder, readOnly, onFocus, onBlur, onInput}; no-op outside the Episode Editor.
  const lock = (field) => (collab ? collab.bind(k(field)) : {});
  const inputLock = (field) => { const { holder, ...rest } = lock(field); return rest; };
  const [title, setTitle] = useSynced(story?.title_telugu || "", k("title_telugu"), collab);
  const [isAuthor, setIsAuthor] = useSynced(perf.is_author !== false, k("is_author"), collab);
  const [writer, setWriter] = useSynced(writerName || "", k("writer"), collab);
  const [storyStart, setStoryStart] = useState(perf.story_start_time || secToHMS(perf.story_start_sec));
  const [storyEnd, setStoryEnd] = useState(perf.story_end_time || secToHMS(perf.story_end_sec));
  const [transcript, setTranscript] = useSynced(perf.transcript || "", k("transcript"), collab);
  const [analysisText, setAnalysisText] = useSynced(perf.analysis_text || "", k("analysis_text"), collab);
  const [genre, setGenre] = useSynced(story?.genre || "", k("genre"), collab);
  const [punchlines, setPunchlines] = useSynced(
    (story?.punchlines && story.punchlines.length > 0)
      ? story.punchlines
      : extractPunchlinesFromAnalysis(perf.analysis_text)
  , k("punchlines"), collab, sameJSON);
  const [segments, setSegments] = useState(storyReel?.highlight_segments || []);
  const [frameTheme, setFrameTheme] = useState(storyReel?.frame_theme || DEFAULT_REEL_FRAME_THEME_ID);
  const [generateMsg, setGenerateMsg] = useState("");

  const displayTitle = title || `(untitled story — performance ${perf.performance_id})`;
  const keyFor = (field) => `${perf.performance_id}:${field}`;

  const savePerf = (field, value) =>
    onSaveField(keyFor(field), null, { table: "performances", match: { performance_id: perf.performance_id }, patch: { [field]: value } });
  const saveStory = (field, value) =>
    story?.story_id && onSaveField(keyFor(field), null, { table: "stories", match: { story_id: story.story_id }, patch: { [field]: value } });

  const handleGenerate = async () => {
    if (!segments.length) {
      setGenerateMsg("Add at least one highlight segment first.");
      return;
    }
    setGenerateMsg("Queuing…");
    const result = await onGenerateReel(story?.story_id, perf.episode_id, segments, frameTheme);
    setGenerateMsg(result.ok ? "Queued — run the local worker to render." : `Failed: ${result.error}`);
  };

  return (
    <div className="border border-kk-gold/15 rounded-xl overflow-hidden bg-white/[0.02]">
      <button onClick={() => setCollapsed((c) => !c)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/[0.03] transition-colors text-left">
        <span className="text-kk-gold/60 text-xs">{collapsed ? <FaChevronRight /> : <FaChevronDown />}</span>
        <div className="flex-1 min-w-0">
          <span className="font-krushna text-kk-cream text-base">"{displayTitle}"</span>
        </div>
        {storyReel?.status && (
          <span className="text-[10px] px-2 py-0.5 rounded bg-kk-gold/10 text-kk-gold/70">{storyReel.status}</span>
        )}
      </button>

      {!collapsed && (
        <div className="px-4 pb-4 space-y-4 border-t border-white/5 pt-3">
          <div>
            <FieldLabel status={saveStatus[keyFor("title_telugu")]}>Story name <LockNote holder={lock("title_telugu").holder} /></FieldLabel>
            <input
              type="text"
              {...inputLock("title_telugu")}
              value={title}
              onChange={(e) => { setTitle(e.target.value); saveStory("title_telugu", e.target.value); }}
              placeholder="కథ పేరు..."
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-kk-cream font-krushna
                placeholder:text-white/20 focus:outline-none focus:ring-1 focus:ring-kk-gold/40"
            />
          </div>

          <div className="flex items-center gap-4 flex-wrap">
            <label className="flex items-center gap-2 text-xs text-white/60 cursor-pointer">
              <input
                type="checkbox"
                checked={isAuthor}
                onChange={(e) => {
                  const v = e.target.checked;
                  setIsAuthor(v);
                  savePerf("is_author", v);
                  if (v) { setWriter(""); saveStory("writer_id", null); }
                }}
                className="accent-kk-gold"
              />
              Storyteller wrote this themselves
            </label>
            {!isAuthor && (
              <div className="flex-1 min-w-[200px]">
                <FieldLabel status={saveStatus[keyFor("writer")]}>Writer <LockNote holder={lock("writer").holder} /></FieldLabel>
                <input
                  type="text"
                  {...inputLock("writer")}
                  value={writer}
                  onChange={(e) => {
                    setWriter(e.target.value);
                    if (story?.story_id) {
                      onSaveField(keyFor("writer"), null, {
                        table: "writer_lookup", story_id: story.story_id, name: e.target.value,
                      });
                    }
                  }}
                  placeholder="రచయిత పేరు..."
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-kk-cream
                    placeholder:text-white/20 focus:outline-none focus:ring-1 focus:ring-kk-gold/40"
                />
              </div>
            )}
          </div>

          {!hideTimestamps && (
          <div className="bg-white/[0.02] border border-white/5 rounded-lg p-3 space-y-2">
            <FieldLabel>Story start / end (full telling, not the reel highlights)</FieldLabel>
            <TimestampRow label="Start" value={storyStart} color="blue" onPlay={onSeek}
              saveStatus={saveStatus[keyFor("story_start_time")]}
              onChange={(v) => setStoryStart(v)}
              onCommit={(v) => { savePerf("story_start_time", v); savePerf("story_start_sec", timeToSec(v)); }}
              onSetFromPlayer={() => {
                const v = secToHMS(currentTime);
                setStoryStart(v);
                savePerf("story_start_time", v);
                savePerf("story_start_sec", timeToSec(v));
              }}
            />
            <TimestampRow label="End" value={storyEnd} color="green" onPlay={onSeek}
              saveStatus={saveStatus[keyFor("story_end_time")]}
              onChange={(v) => setStoryEnd(v)}
              onCommit={(v) => { savePerf("story_end_time", v); savePerf("story_end_sec", timeToSec(v)); }}
              onSetFromPlayer={() => {
                const v = secToHMS(currentTime);
                setStoryEnd(v);
                savePerf("story_end_time", v);
                savePerf("story_end_sec", timeToSec(v));
              }}
            />
          </div>
          )}

          <div>
            <button
              type="button"
              onClick={() => setShowText((v) => !v)}
              className="flex items-center gap-2 text-xs font-semibold text-kk-gold/70 hover:text-kk-gold transition-colors"
            >
              {showText ? <FaChevronDown className="text-[10px]" /> : <FaChevronRight className="text-[10px]" />}
              Transcript &amp; Literary Analysis
              <span className="text-[10px] text-white/25 font-normal">(hidden by default — click to show)</span>
            </button>
          </div>

          {showText && (
            <>
              <div>
                <FieldLabel status={saveStatus[keyFor("transcript")]}>Transcript <LockNote holder={lock("transcript").holder} /></FieldLabel>
                <textarea
                  {...inputLock("transcript")}
                  value={transcript}
                  onChange={(e) => { setTranscript(e.target.value); savePerf("transcript", e.target.value); }}
                  rows={5}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-kk-cream
                    placeholder:text-white/20 focus:outline-none focus:ring-1 focus:ring-kk-gold/40 resize-y font-mono"
                />
              </div>

              <div>
                <FieldLabel status={saveStatus[keyFor("analysis_text")]}>Literary analysis <LockNote holder={lock("analysis_text").holder} /></FieldLabel>
                <textarea
                  {...inputLock("analysis_text")}
                  value={analysisText}
                  onChange={(e) => { setAnalysisText(e.target.value); savePerf("analysis_text", e.target.value); }}
                  rows={6}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-kk-cream
                    placeholder:text-white/20 focus:outline-none focus:ring-1 focus:ring-kk-gold/40 resize-y font-mono"
                />
              </div>
            </>
          )}

          <div>
            <FieldLabel status={saveStatus[keyFor("genre")]}>Genre <LockNote holder={lock("genre").holder} /></FieldLabel>
            <input
              list="genre-suggestions"
              {...inputLock("genre")}
              value={genre}
              onChange={(e) => { setGenre(e.target.value); saveStory("genre", e.target.value); }}
              placeholder="హాస్యం, భయానక, ..."
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-kk-cream
                placeholder:text-white/20 focus:outline-none focus:ring-1 focus:ring-kk-gold/40"
            />
            <datalist id="genre-suggestions">
              {GENRE_SUGGESTIONS.map((g) => <option key={g} value={g} />)}
            </datalist>
          </div>

          <div>
            <FieldLabel status={saveStatus[keyFor("punchlines")]}>Punch lines <LockNote holder={lock("punchlines").holder} /></FieldLabel>
            <div onFocus={lock("punchlines").onFocus} onBlur={lock("punchlines").onBlur} onInput={lock("punchlines").onInput}
              className={lock("punchlines").holder ? "pointer-events-none opacity-60" : ""}>
            <PunchlinesEditor
              punchlines={punchlines}
              onSeek={onSeek}
              onChange={(next) => { setPunchlines(next); saveStory("punchlines", next); }}
            />
            </div>
          </div>

          <div>
            <FieldLabel status={saveStatus[keyFor("segments")]}>Highlight segments (for the reel)</FieldLabel>
            <HighlightSegmentsEditor
              segments={segments}
              currentTime={currentTime}
              onSeek={onSeek}
              onChange={(next) => {
                setSegments(next);
                onSaveField(keyFor("segments"), null, {
                  table: "story_reels", storyReel, story_id: story?.story_id, episode_id: perf.episode_id,
                  patch: { highlight_segments: next },
                });
              }}
            />
          </div>

          <div>
            <FieldLabel>Reel frame theme</FieldLabel>
            <FrameThemePicker
              value={frameTheme}
              onChange={(id) => {
                setFrameTheme(id);
                onSaveField(keyFor("frame_theme"), null, {
                  table: "story_reels", storyReel, story_id: story?.story_id, episode_id: perf.episode_id,
                  patch: { frame_theme: id },
                });
              }}
            />
          </div>

          <div className="flex items-center gap-3 pt-1">
            <button
              onClick={handleGenerate}
              disabled={!story?.story_id}
              className="flex items-center gap-2 px-4 py-2 bg-kk-gold text-kk-ink rounded-lg font-semibold text-sm
                hover:bg-kk-amber active:scale-95 transition-all disabled:opacity-40"
            >
              <FaFilm className="text-xs" /> Generate Reel
            </button>
            {generateMsg && <span className="text-xs text-white/50">{generateMsg}</span>}
          </div>
        </div>
      )}
    </div>
  );
};

export default StoryCard;
export { SaveIndicator, FieldLabel, TimestampRow, extractPunchlinesFromAnalysis, GENRE_SUGGESTIONS };
